import { ITEM_TYPES, itemUsesColor, type ItemType } from '@brickloop/core/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api, ApiError, qs, type ItemLookupResponse } from '../api';
import { CameraScan } from '../components/CameraScan';
import { ColourPicker } from '../components/ColourPicker';
import { TYPE_LABEL } from '../lib/labels';
import { useSettled } from '../lib/useSettled';
import { useCategories, useColors } from '../queries';
import { Button, Card, ErrorMessage, Field, Input, Notice, PageHeader, Select } from '../ui';

/** Its own page, so nothing else competes for attention while an item is being made. */
export function NewItem() {
  const navigate = useNavigate();
  return (
    <>
      <PageHeader
        title="New item"
        subtitle={
          <>
            <Link to="/items" className="hover:underline">
              Items
            </Link>{' '}
            / New
          </>
        }
      />
      <NewItemForm onDone={() => undefined} onCancel={() => navigate('/items')} />
    </>
  );
}

function NewItemForm({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const categories = useCategories();
  const colours = useColors();
  const emptyCatalogue = (colours.data?.length ?? 1) === 0 || (categories.data?.length ?? 1) === 0;
  // The colour and category names live in the catalogue, so without them BrickLink's answers cannot be named.
  const seed = useMutation({
    mutationFn: () => api('/api/v1/catalog/seed-bricklink', { method: 'POST', body: {} }),
    onSuccess: () => {
      for (const k of ['colors', 'categories', 'item-lookup', 'item-colors']) void qc.invalidateQueries({ queryKey: [k] });
    },
  });
  const [itemType, setItemType] = useState<ItemType>('PART');
  const [itemNo, setItemNo] = useState('');
  const [name, setName] = useState('');
  const [categoryText, setCategoryText] = useState('');
  const [weight, setWeight] = useState('');
  const [year, setYear] = useState('');
  const [colorIds, setColorIds] = useState<number[]>([]);

  const category = categories.data?.find((c) => c.name.toLowerCase() === categoryText.trim().toLowerCase()) ?? null;

  // Once the part number settles, BrickLink is asked for its name, category, weight and year, and whatever is still
  // blank is filled in. It is asked fresh and not kept; the form saves whatever you accept.
  const settled = useSettled(itemNo.trim());
  const lookup = useQuery({
    queryKey: ['item-lookup', itemType, settled],
    enabled: !!settled,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: () => api<ItemLookupResponse>(`/api/v1/bricklink/item${qs({ itemType, itemNo: settled })}`),
  });
  const found = lookup.data?.item;
  useEffect(() => {
    if (!found) return;
    if (found.name) setName(found.name);
    if (found.categoryName) setCategoryText(found.categoryName);
    if (found.weightG !== null) setWeight(String(found.weightG));
    if (found.yearReleased !== null) setYear(String(found.yearReleased));
  }, [found]);
  // What BrickLink supplied is locked, so the item matches BrickLink. Anything it did not supply stays editable.
  const locked = {
    name: !!found?.name,
    category: !!found?.categoryName,
    weight: found?.weightG != null,
    year: found?.yearReleased != null,
  };
  const asking = !!itemNo.trim() && (settled !== itemNo.trim() || lookup.isFetching);

  const create = useMutation({
    mutationFn: () =>
      api<{ item: { id: number } }>('/api/v1/items', {
        body: {
          itemType,
          itemNo: itemNo.trim(),
          name: name.trim(),
          categoryId: category?.id ?? null,
          weightG: weight.trim() ? Number(weight) : null,
          yearReleased: year.trim() ? Number(year) : null,
          colorIds: itemUsesColor(itemType) ? colorIds : [],
          ...(found && {
            dimX: found.dimX,
            dimY: found.dimY,
            dimZ: found.dimZ,
            alternateNo: found.alternateNo,
            description: found.description,
            isObsolete: found.isObsolete,
            fromBricklink: true,
          }),
        },
      }),
    onSuccess: ({ item }) => {
      void qc.invalidateQueries({ queryKey: ['items'] });
      onDone();
      navigate(`/items/${item.id}`);
    },
  });

  const exists = create.error instanceof ApiError && create.error.code === 'item_exists' ? (create.error.details as { id: number }).id : (lookup.data?.existingId ?? null);
  const canCreate = !!itemNo.trim() && !!name.trim() && !create.isPending;

  return (
    <div className="space-y-4">
    <CameraScan
      identifyOnly
      colors={colours.data ?? []}
      onAdd={(pick) => {
        setItemType(pick.itemType);
        setItemNo(pick.itemNo);
        if (pick.itemName) setName(pick.itemName);
        const c = colours.data?.find((x) => x.name === pick.colorName);
        if (c && itemUsesColor(pick.itemType)) setColorIds((ids) => (ids.includes(c.id) ? ids : [...ids, c.id]));
      }}
    />
    <Card>
      <p className="mb-3 text-sm text-slate-600">
        An item can be created on its own, with no purchase behind it. Tick the colours you keep it in and each becomes a child you can stock, order and set a default bin for.
      </p>
      {emptyCatalogue && (
        <div className="mb-3 space-y-2">
          <Notice tone="amber">
            The catalogue has no colours or categories yet, so BrickLink's category and colours cannot be shown by name. Load them once (two requests to BrickLink).
            <div className="mt-2">
              <Button variant="secondary" disabled={seed.isPending} onClick={() => seed.mutate()}>
                {seed.isPending ? 'Loading…' : 'Load colours and categories from BrickLink'}
              </Button>
            </div>
          </Notice>
          <ErrorMessage error={seed.error} />
        </div>
      )}
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (canCreate) create.mutate();
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Type">
            <Select
              value={itemType}
              onChange={(e) => {
                setItemType(e.target.value as ItemType);
                setColorIds([]);
              }}
            >
              {ITEM_TYPES.map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABEL[t]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Item number" hint="As BrickLink writes it, e.g. 3001 or 75192-1. Its details are looked up once you stop typing.">
            <Input
              required
              autoComplete="off"
              value={itemNo}
              onChange={(e) => {
                setItemNo(e.target.value);
                setColorIds([]);
              }}
            />
          </Field>
        </div>

        {asking ? (
          <p className="text-sm text-slate-600">Asking BrickLink about {itemNo.trim()}…</p>
        ) : lookup.data ? (
          lookup.data.item ? (
            <Notice tone="green">BrickLink calls it “{lookup.data.item.name}”, in the category {lookup.data.item.categoryName ?? 'it does not name here'}. Those fields are locked; fill in any it did not supply.</Notice>
          ) : lookup.data.notOnBrickLink ? (
            <Notice tone="amber">BrickLink does not know that number. Fill the details in by hand.</Notice>
          ) : (
            <Notice tone="amber">{lookup.data.message ?? 'BrickLink could not be asked. Fill the details in by hand.'}</Notice>
          )
        ) : null}
        <ErrorMessage error={lookup.error} />
        {exists !== null && (
          <Notice tone="amber">
            That item is already in your catalogue.{' '}
            <Link to={`/items/${exists}`} className="font-medium underline">
              Open it
            </Link>{' '}
            to add colours.
          </Notice>
        )}

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Name" className="sm:col-span-2">
            <Input required readOnly={locked.name} className={locked.name ? 'bg-slate-100' : undefined} value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Category" hint={categoryText.trim() && !category ? 'Not a category in the catalogue, so it will be left blank.' : undefined}>
            <Input list={locked.category ? undefined : 'item-categories'} readOnly={locked.category} className={locked.category ? 'bg-slate-100' : undefined} value={categoryText} onChange={(e) => setCategoryText(e.target.value)} />
            <datalist id="item-categories">
              {categories.data?.map((c) => (
                <option key={c.id} value={c.name} />
              ))}
            </datalist>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Weight (g)">
              <Input inputMode="decimal" readOnly={locked.weight} className={locked.weight ? 'bg-slate-100' : undefined} value={weight} onChange={(e) => setWeight(e.target.value)} />
            </Field>
            <Field label="Year">
              <Input inputMode="numeric" readOnly={locked.year} className={locked.year ? 'bg-slate-100' : undefined} value={year} onChange={(e) => setYear(e.target.value)} />
            </Field>
          </div>
        </div>

        {found && (found.dimX !== null || found.alternateNo || found.isObsolete !== null || found.description) && (
          <dl className="grid gap-x-6 gap-y-1 rounded-md bg-slate-50 px-3 py-2 text-sm sm:grid-cols-2">
            {found.dimX !== null && (
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Dimensions (from BrickLink)</dt>
                <dd>{[found.dimX, found.dimY, found.dimZ].map((d) => d ?? '?').join(' × ')}</dd>
              </div>
            )}
            {found.alternateNo && (
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Alternate numbers</dt>
                <dd>{found.alternateNo}</dd>
              </div>
            )}
            {found.isObsolete !== null && (
              <div className="flex justify-between gap-3">
                <dt className="text-slate-500">Obsolete</dt>
                <dd>{found.isObsolete ? 'Yes' : 'No'}</dd>
              </div>
            )}
            {found.description && (
              <div className="sm:col-span-2">
                <dt className="text-slate-500">Description</dt>
                <dd>{found.description}</dd>
              </div>
            )}
          </dl>
        )}

        <div>
          <h3 className="mb-1.5 text-sm font-medium text-slate-700">Colours</h3>
          <ColourPicker itemType={itemType} itemNo={itemNo} selected={colorIds} onChange={setColorIds} />
        </div>

        <ErrorMessage error={create.error} />
        <div>
          <Button variant="ghost" className="mr-2" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={!canCreate}>
            {create.isPending ? 'Creating…' : itemUsesColor(itemType) && colorIds.length > 0 ? `Create item and ${colorIds.length} ${colorIds.length === 1 ? 'colour' : 'colours'}` : 'Create item'}
          </Button>
        </div>
      </form>
    </Card>
    </div>
  );
}
